import { apiVersion } from './apis.const';
import { transformResponse } from './apis.util';
import { baseApi } from './base.api';

/**
 * API Urls
 */
const loginUrl = `/${apiVersion}/login`;
const forgotPasswordUrl = `/${apiVersion}/forgot-password`;
const resetPasswordUrl = `/${apiVersion}/reset-password`;
const getEmailVerificationLinkUrl = `/${apiVersion}/email/verification-notification`;
const verifyEmailUrl = (link: string) => `${link.replace(process.env.REACT_APP_API_BASE_URL as string, '')}`;

/**
 * Create APIs
 */
export const authApi = baseApi.injectEndpoints({
  overrideExisting: false,

  endpoints: (build) => ({
    authLogin: build.mutation<AuthLoginRes, AuthLoginReq>({
      query: (body) => ({
        url: loginUrl,
        method: 'POST',
        body,
      }),
      transformResponse,
    }),

    authForgotPassword: build.mutation<AuthForgotPasswordRes, AuthForgotPasswordReq>({
      query: (body) => ({
        url: forgotPasswordUrl,
        method: 'POST',
        body,
      }),
      transformResponse,
    }),

    authResetPassword: build.mutation<AuthResetPasswordRes, AuthResetPasswordReq>({
      query: (body) => ({
        url: resetPasswordUrl,
        method: 'POST',
        body,
      }),
      transformResponse,
    }),

    authGetEmailVerificationLink: build.mutation<AuthGetEmailVerificationLinkRes, AuthGetEmailVerificationLinkReq>({
      query: () => ({
        url: getEmailVerificationLinkUrl,
        method: 'POST',
      }),
      transformResponse,
    }),

    authVerifyEmail: build.query<AuthVerifyEmailRes, AuthVerifyEmailReq>({
      query: ({ link }) => ({
        url: verifyEmailUrl(link),
      }),
      transformResponse,
    }),

    authVerifyEmployeeCode: build.query<AuthVerifyEmployeeCodeRes, AuthVerifyEmployeeCodeReq>({
      query: ({ code, zoneId }) => ({
        url: '/v1/employee/verify',
        method: 'POST',
        body: {
          code,
          zoneId,
        },
      }),
      transformResponse,
    }),
  }),
});

/**
 * Hooks
 */
export const {
  useAuthLoginMutation,
  useAuthForgotPasswordMutation,
  useAuthResetPasswordMutation,
  useAuthGetEmailVerificationLinkMutation,
  useAuthVerifyEmailQuery,
  useLazyAuthVerifyEmployeeCodeQuery,
} = authApi;

/**
 * Types
 */
type ImageConfig = {
  id: number;
  name: string;
  url: string;
};
export type ProfilePhoto = ImageConfig;
export type OrganizationLogo = ImageConfig;
export type Address = {
  id: number | null;
  address_1: string | null;
  address_2: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
};
export type OrganizationBilling = {
  id: number;
  stripe_id: string;
  pm_type: string;
  pm_last_four: string | null;
  trial_ends_at: string;
  name: string;
  email: string;
  terms: string;
  billing_address_id: number;
  shipping_address_id: number;
  billing_address: Address | null;
  shipping_address: Address | null;
};
export type Organization = {
  id: number;
  name: string;
  slug: string;
  primary_brand_color: string | null;
  secondary_brand_color: string | null;
  created_at: string;
  updated_at: string;
  selected_logo: File | null;
  logo: OrganizationLogo | null;
  organization_billing: OrganizationBilling | null;
};
type UserRole = any;
export type User = {
  id: number;
  name: string;
  email: string;
  email_verified_at: string | null;
  phone: string | null;
  title: string | null;
  profile_photo: ProfilePhoto | null;
  default_organization_id: number | null;
  default_organization: Organization;
  super_admin: number | null;
  client_id: number | null;
  code: string | null;
  otp_enabled: boolean | null;
  has_subscription: boolean | null;
  deleted_at: string | null;
  created_at: string | null;
  created_by: number | null;
  updated_at: string | null;
  updated_by: number | null;
  roles: UserRole[];
};
export type AuthLoginRes = {
  authenticated: boolean;
  user: User;
};
type AuthLoginReq = {
  email: string;
  password: string;
};
type AuthForgotPasswordRes = any;
type AuthForgotPasswordReq = {
  email: string;
};
type AuthResetPasswordRes = any;
type AuthResetPasswordReq = {
  email: string;
  token: string;
  password: string;
  password_confirmation: string;
};
type AuthGetEmailVerificationLinkRes = any;
type AuthGetEmailVerificationLinkReq = void;
type AuthVerifyEmailRes = any;
type AuthVerifyEmailReq = {
  link: string;
};
type AuthVerifyEmployeeCodeRes = any;
type AuthVerifyEmployeeCodeReq = {
  code: string;
  zoneId: number;
};
